import { useEffect } from "react";
import { buildMediaMetadata, MEDIA_SEEK_SECONDS, seekAudio, validPositionState } from "../lib/playerMediaSession";

export default function usePlayerMediaSession({
  audioRef,
  currentTrack,
  isPlaying,
  play,
  pause,
  previous,
  next,
  enabled = true,
}) {
  useEffect(() => {
    const mediaSession = typeof navigator !== "undefined" ? navigator.mediaSession : null;
    const audio = audioRef?.current;
    if (!enabled) {
      if (mediaSession) {
        ["play", "pause", "previoustrack", "nexttrack", "seekbackward", "seekforward", "seekto"].forEach((action) => {
          try { mediaSession.setActionHandler(action, null); } catch {}
        });
        try { mediaSession.metadata = null; mediaSession.playbackState = "none"; } catch {}
      }
      return undefined;
    }
    if (!mediaSession || !audio) return undefined;

    try {
      if (typeof MediaMetadata === "function") {
        mediaSession.metadata = new MediaMetadata(buildMediaMetadata(currentTrack, window.location.origin));
      }
      mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch {}

    const handlers = {
      play,
      pause,
      previoustrack: previous,
      nexttrack: next,
      seekbackward: (details = {}) => seekAudio(audio, audio.currentTime - (details.seekOffset || MEDIA_SEEK_SECONDS)),
      seekforward: (details = {}) => seekAudio(audio, audio.currentTime + (details.seekOffset || MEDIA_SEEK_SECONDS)),
      seekto: (details = {}) => {
        if (!Number.isFinite(Number(details.seekTime))) return;
        if (details.fastSeek && typeof audio.fastSeek === "function") audio.fastSeek(Number(details.seekTime));
        else seekAudio(audio, Number(details.seekTime));
      },
    };

    Object.entries(handlers).forEach(([action, handler]) => {
      try { mediaSession.setActionHandler(action, handler); } catch {}
    });

    let lastPositionUpdate = 0;
    const updatePosition = () => {
      const now = Date.now();
      if (now - lastPositionUpdate < 900) return;
      lastPositionUpdate = now;
      const state = validPositionState(audio);
      if (!state || typeof mediaSession.setPositionState !== "function") return;
      try { mediaSession.setPositionState(state); } catch {}
    };
    ["loadedmetadata", "durationchange", "timeupdate", "ratechange", "seeked"].forEach((event) =>
      audio.addEventListener(event, updatePosition)
    );
    updatePosition();

    return () => {
      Object.keys(handlers).forEach((action) => {
        try { mediaSession.setActionHandler(action, null); } catch {}
      });
      ["loadedmetadata", "durationchange", "timeupdate", "ratechange", "seeked"].forEach((event) =>
        audio.removeEventListener(event, updatePosition)
      );
    };
  }, [audioRef, currentTrack, enabled, isPlaying, next, pause, play, previous]);
}
